// คอยน์ (ledger), เติมเงิน, เช็คอิน, โค้ด, ชวนเพื่อน, VIP — พอร์ตจาก Economy.gs
//  หักคอยน์ด้วย transaction (ETag) บน /users/<uid>/coins แล้วเขียน /coin_tx/<uid>/<id> ทุกครั้ง
import { ENDPOINTS } from "../config";
import { http } from "../apiClient";
import {
  fb, fk, err, str, num, nowIso, dayKey, monthKey, limitOf, pick, getRow, rowsOf, putNew, updateRow,
  readAll, settings, setting, pageByKey, pageRows, acquireFlag, releaseFlag, rateLimit, bump, asArray,
} from "./core";
import { getUser } from "./auth";
import { coinCalc, creditCalc, creditPresets, parseTiers } from "./pricing";

// ---------------------------------------------------------------------------
export async function changeCoins(userId, delta, meta = {}) {
  let before = 0, after = 0;
  await fb.transaction(`users/${fk(userId)}/coins`, (cur) => {
    if (cur === null) throw err("not_found", "ไม่พบผู้ใช้");
    before = num(cur);
    after = before + delta;
    if (after < 0) throw err("insufficient_coins", `คอยน์ไม่พอ ต้องการ ${-delta} คอยน์ แต่มี ${before} คอยน์`);
    return after;
  });
  const tx = await putNew(`coin_tx/${fk(userId)}`, "tx", {
    userId, usageType: meta.usageType || "misc", amount: delta, balanceBefore: before, balanceAfter: after,
    refType: meta.refType, refId: meta.refId, chatId: meta.chatId, characterId: meta.characterId,
    modelId: meta.modelId, note: meta.note, actorId: meta.actorId,
  });
  if (delta < 0 && (meta.usageType === "message" || meta.usageType === "gift")) {
    await bump("coinsSpent", null, -delta);
    if (meta.usageType === "message") await bump("messages", null, 1);
  }
  return { balance: after, tx };
}

export async function notify(userId, category, title, body, link) {
  try {
    await putNew(`notifications/${fk(userId)}`, "nt", { userId, category, title, body: body || "", link: link || "", isRead: false });
  } catch (e) {
    /* แจ้งเตือนล้มไม่ควรทำให้งานหลักล้ม */
  }
}

// ---------------------------------------------------------------------------
// VIP (คุยคลับ)
// ---------------------------------------------------------------------------
export function prevMonthKey(mk) {
  let y = parseInt(mk.slice(0, 4), 10), m = parseInt(mk.slice(5, 7), 10) - 1;
  if (m === 0) {
    y--;
    m = 12;
  }
  return y + "-" + ("0" + m).slice(-2);
}

export const sortedTiers = async () => (await readAll("vip_tiers")).slice().sort((a, b) => num(a.minPoints) - num(b.minPoints));

/** สถานะ VIP ของผู้ใช้ (ม้วนเดือนให้แบบเสมือน ไม่เขียน) */
export async function vipState(user) {
  const mk = monthKey();
  let cur = num(user.vipPointsCur), prev = num(user.vipPointsPrev);
  const m = user.vipMonth || mk;
  if (m !== mk) {
    prev = m === prevMonthKey(mk) ? cur : 0;
    cur = 0;
  }
  const eff = Math.max(cur, prev), tiers = await sortedTiers();
  let idx = 0;
  tiers.forEach((t, i) => {
    if (eff >= num(t.minPoints)) idx = i;
  });
  return {
    tier: tiers[idx] || { id: "rookie", name: "Rookie", minPoints: 0, monthlyCoins: 0, checkinMultiplier: 1, noteLimit: 7800 },
    tierIndex: idx, nextTier: tiers[idx + 1] || null, tiers,
    currentMonthPoints: cur, previousMonthPoints: prev, effectivePoints: eff, month: mk,
  };
}

export const vipPublic = (v) => ({
  tier: v.tier, nextTier: v.nextTier, currentMonthPoints: v.currentMonthPoints, previousMonthPoints: v.previousMonthPoints,
  effectivePoints: v.effectivePoints, tierIndex: v.tierIndex,
});

const claimableMonthly = (u, v) => {
  const claimed = asArray((u.monthlyClaims || {})[v.month]);
  return v.tiers.filter((t, i) => i <= v.tierIndex && num(t.monthlyCoins) > 0 && claimed.indexOf(t.id) === -1);
};

export async function vipStatus(p, ctx) {
  const u = await getUser(ctx.uid), v = await vipState(u);
  const out = vipPublic(v);
  out.tiers = v.tiers;
  const claimable = claimableMonthly(u, v);
  out.canClaimMonthly = claimable.length > 0;
  out.monthlyCoins = claimable.reduce((s, t) => s + num(t.monthlyCoins), 0);
  return out;
}

export async function vipClaimMonthly(p, ctx) {
  const u = await getUser(ctx.uid), v = await vipState(u), list = claimableMonthly(u, v);
  if (!list.length) throw err("already_done", "ไม่มีคอยน์รายเดือนให้รับ");
  let granted = [];
  // จองสิทธิ์แบบ atomic บน monthlyClaims/<เดือน> — กดซ้ำ/สองแท็บพร้อมกันได้ครั้งเดียว
  await fb.transaction(`users/${fk(ctx.uid)}/monthlyClaims/${v.month}`, (cur) => {
    const claimed = asArray(cur);
    granted = list.filter((t) => claimed.indexOf(t.id) === -1);
    if (!granted.length) throw err("already_done", "ไม่มีคอยน์รายเดือนให้รับ");
    return claimed.concat(granted.map((t) => t.id));
  });
  const total = granted.reduce((s, t) => s + num(t.monthlyCoins), 0);
  const r = await changeCoins(ctx.uid, total, { usageType: "monthly", refType: "vip", refId: v.month, note: "คอยน์รายเดือนคุยคลับ" });
  return { coins: total, balance: r.balance };
}

// ---------------------------------------------------------------------------
// แพ็กเกจ / โมเดล / เติมเงิน
// ---------------------------------------------------------------------------
export function packageView(pk) {
  const coins = num(pk.baseCoins) + num(pk.bonusCoins);
  return {
    id: pk.id, name: pk.name, icon: pk.icon, tagline: pk.tagline, price: num(pk.price), baseCoins: num(pk.baseCoins),
    bonusCoins: num(pk.bonusCoins), bonusPercent: num(pk.bonusPercent), coins, vipPoints: num(pk.vipPoints),
    maxMessages: num(pk.maxMessages), tag: pk.tag,
  };
}

export async function packagesList() {
  const rows = (await readAll("packages")).filter((p) => p.isActive !== false).sort((a, b) => num(a.sort) - num(b.sort));
  return { items: rows.map(packageView) };
}

export async function modelsList() {
  const rows = (await readAll("ai_models")).filter((m) => m.isActive !== false).sort((a, b) => num(a.sort) - num(b.sort));
  return { items: rows.map((m) => pick(m, ["id", "name", "category", "coinPerMessage", "description", "isNew", "isDefault"])) };
}

export const CUSTOM_PACKAGE_ID = "custom";

/** ค่าตั้งราคาจาก settings (แอดมินแก้ได้) -> รูปที่ pricing.js ใช้ */
export async function pricingConfig() {
  const st = await settings();
  return {
    coin: { perBaht: num(st.coin_per_baht, 10), min: num(st.custom_topup_min, 300), max: num(st.custom_topup_max, 50000), bonusTiers: st.coin_bonus_tiers },
    credit: { price: num(st.create_credit_price, 300), max: num(st.create_credit_max, 100), tiers: st.create_credit_tiers, required: st.create_credit_required !== false },
  };
}

/** ราคา/ขั้นส่วนลดทั้งหมดให้หน้าเว็บแสดง + จำนวนสิทธิ์สร้างตัวละครคงเหลือของผู้ใช้ */
export async function pricingGet(p, ctx) {
  const c = await pricingConfig();
  const u = await getUser(ctx.uid);
  return {
    coin: { ...c.coin, bonusTiers: parseTiers(c.coin.bonusTiers) },
    create: { ...c.credit, tiers: parseTiers(c.credit.tiers), presets: creditPresets(c.credit) },
    createCredits: num(u && u.createCredits), isSuper: !!ctx.isSuper,
  };
}

/** ผูก transactionId กับผู้ใช้+แพ็ก ก่อนจ่ายเงิน (ราคาอ้างจาก /packages หรือสูตรกำหนดเอง ไม่รับคอยน์จาก client) */
export async function topupInit(p, ctx) {
  const txId = str(p.transactionId, 100, "transactionId", true);
  const packageId = str(p.packageId, 60, "packageId", true);
  const now = nowIso();
  let row;
  if (packageId === CUSTOM_PACKAGE_ID) {
    const calc = coinCalc(p.amount, (await pricingConfig()).coin);
    if (!calc.ok) throw err("bad_request", calc.reason);
    row = { id: txId, userId: ctx.uid, packageId, kind: "coin_custom", amount: calc.baht, coins: calc.coins, points: calc.vipPoints, status: "pending", createdAt: now, updatedAt: now };
  } else {
    const pk = await getRow(`packages/${fk(packageId)}`);
    if (!pk || pk.isActive === false) throw err("not_found", "ไม่พบแพ็กเกจ");
    const v = packageView(pk);
    const minBaht = (await pricingConfig()).coin.min;
    if (v.price < minBaht) throw err("bad_request", `แพ็กนี้ต่ำกว่ายอดเติมขั้นต่ำ ${minBaht} บาท`);
    row = { id: txId, userId: ctx.uid, packageId: pk.id, amount: v.price, coins: v.coins, points: v.vipPoints, status: "pending", createdAt: now, updatedAt: now };
  }
  if (await fb.createOnly(`topups/${fk(txId)}`, row)) return { topup: { id: txId, status: "pending", amount: row.amount } };
  const ex = await getRow(`topups/${fk(txId)}`);
  if (ex && ex.userId !== ctx.uid) throw err("forbidden", "transactionId นี้ถูกใช้แล้ว");
  return { topup: { id: ex.id, status: ex.status, amount: num(ex.amount) } };
}

export const PAID_STATUSES = ["success", "settled", "completed"];

/** ถาม PaymentBroker จากหน้าเว็บ: endpoint เดียวกับ getTransaction ของ CRM (ส่ง Authorization ของผู้ใช้) */
async function fetchDeposit(txId) {
  const res = await http.get(ENDPOINTS.depositGetById, { uid: txId });
  if (!res.status || !res.data) throw err("payment_pending", "ยังไม่พบรายการชำระเงิน");
  return { status: String(res.data.status || "").toLowerCase(), amount: num(res.data.amount) };
}

export async function topupConfirm(p, ctx) {
  const txId = str(p.transactionId, 100, "transactionId", true);
  const key = `topups/${fk(txId)}`;
  const existing = await getRow(key);
  if (existing && existing.userId !== ctx.uid) throw err("forbidden", "รายการนี้ไม่ใช่ของคุณ");
  if (existing && existing.kind === "create_credit") return creditPackConfirm(p, ctx); // ลิงก์ "ตรวจสอบ" เก่า/ปนทาง : ส่งไปทางสิทธิ์สร้างตัวละคร
  if (existing && existing.status === "credited") return { credited: true, already: true, coins: num(existing.coins), points: num(existing.points) };
  if (!existing && (await setting("topup_require_init")) !== false) throw err("bad_request", "ไม่พบรายการเติมเงินนี้ (ต้องเรียก topup.init หลังสร้าง QR)");
  if (!(await acquireFlag("tx/" + txId, 60))) throw err("chat_busy", "กำลังตรวจสอบรายการนี้อยู่ ลองใหม่อีกสักครู่");
  try {
    const packageId = existing ? existing.packageId : str(p.packageId, 60, "packageId", true);
    const custom = packageId === CUSTOM_PACKAGE_ID;
    const pk = custom ? { id: CUSTOM_PACKAGE_ID, name: "กำหนดเอง" } : await getRow(`packages/${fk(packageId)}`);
    if (!pk) throw err("not_found", "ไม่พบแพ็กเกจ");
    const dep = await fetchDeposit(txId);
    if (PAID_STATUSES.indexOf(dep.status) === -1) throw err("payment_pending", "ยังไม่พบการชำระเงิน (สถานะ: " + (dep.status || "ไม่ทราบ") + ")");
    let view;
    if (custom) {
      // กำหนดเอง : คำนวณคอยน์ใหม่จาก "ยอดที่จ่ายจริง" (ไม่เชื่อค่าจาก client/แถว init)
      const calc = coinCalc(dep.amount, (await pricingConfig()).coin);
      if (!calc.ok) throw err("bad_request", "ยอดชำระไม่อยู่ในช่วงที่กำหนดเอง: " + calc.reason);
      if (existing && Math.abs(num(existing.amount) - dep.amount) > 0.009) throw err("bad_request", "ยอดชำระไม่ตรงกับที่แจ้งไว้ตอนสร้าง QR");
      view = { price: calc.baht, coins: calc.coins, vipPoints: calc.vipPoints, name: `กำหนดเอง ${calc.baht} บาท` };
    } else {
      view = packageView(pk);
      if (pk.isActive === false && !existing) throw err("not_found", "แพ็กเกจนี้ปิดใช้งานแล้ว");
      if (Math.abs(dep.amount - view.price) > 0.009) throw err("bad_request", "ยอดชำระไม่ตรงกับราคาแพ็กเกจ");
    }

    // จองการเติม (pending -> credited) แบบ atomic ก่อนเติมคอยน์ — เรียกซ้ำ/หลายแท็บจะเติมได้ครั้งเดียว
    let claimed = false;
    const now = nowIso();
    await fb.transaction(key, (cur) => {
      if (cur && cur.status === "credited") return undefined;
      claimed = true;
      return {
        ...(cur || { id: txId, createdAt: now }), userId: ctx.uid, packageId: pk.id, ...(custom ? { kind: "coin_custom" } : {}), amount: view.price, coins: view.coins,
        points: view.vipPoints, status: "credited", providerStatus: dep.status, creditedAt: now, updatedAt: now,
      };
    });
    if (!claimed) return { credited: true, already: true, coins: view.coins, points: view.vipPoints };

    let r;
    try {
      r = await changeCoins(ctx.uid, view.coins, { usageType: "purchase", refType: "topup", refId: txId, note: "เติมเงิน " + view.name });
    } catch (e) {
      // เติมไม่สำเร็จ -> คืนสถานะ pending ให้ลองยืนยันใหม่ได้
      await updateRow(key, { status: "pending", creditedAt: null }).catch(() => {});
      throw e;
    }
    try {
      const mk = monthKey();
      await updateRow(`users/${fk(ctx.uid)}`, (u) => {
        let cur = num(u.vipPointsCur), prev = num(u.vipPointsPrev);
        const m = u.vipMonth || mk;
        if (m !== mk) {
          prev = m === prevMonthKey(mk) ? cur : 0;
          cur = 0;
        }
        return { vipPointsCur: cur + view.vipPoints, vipPointsPrev: prev, vipMonth: mk, lifetimePoints: num(u.lifetimePoints) + view.vipPoints };
      });
    } catch (e) {
      /* พ้อยท์ VIP ล้มไม่ควรทำให้การเติมคอยน์ที่สำเร็จแล้วดูเหมือนล้ม */
    }
    return { credited: true, coins: view.coins, added: view.coins, points: view.vipPoints, balance: r.balance };
  } finally {
    await releaseFlag("tx/" + txId);
  }
}

export async function topupsMine(p, ctx) {
  const rows = rowsOf(await fb.get("topups", { orderBy: "userId", equalTo: ctx.uid }));
  return pageRows(rows, { limit: limitOf(p.limit, 20), cursor: p.cursor, sort: "createdAt" });
}

// ---------------------------------------------------------------------------
// สิทธิ์สร้างตัวละคร (createCredits) — ซื้อด้วย QR แบบเดียวกับเติมคอยน์ ; เผยแพร่ตัวละครครั้งแรกหัก 1 สิทธิ์ (content.js)
// ---------------------------------------------------------------------------
/** เพิ่ม/หักสิทธิ์ด้วย transaction (ETag) บน users/<uid>/createCredits แล้วบันทึก credit_tx/<uid>/<id> */
export async function changeCredits(userId, delta, meta = {}) {
  let before = 0, after = 0;
  await fb.transaction(`users/${fk(userId)}/createCredits`, (cur) => {
    before = num(cur);
    after = before + delta;
    if (after < 0) throw err("insufficient_credits", `สิทธิ์สร้างตัวละครไม่พอ ต้องการ ${-delta} สิทธิ์ แต่มี ${before} สิทธิ์`);
    return after;
  });
  const tx = await putNew(`credit_tx/${fk(userId)}`, "ct", {
    userId, type: meta.type || "misc", amount: delta, balanceBefore: before, balanceAfter: after, refType: meta.refType, refId: meta.refId, note: meta.note,
  });
  return { balance: after, tx };
}

export async function creditPackInit(p, ctx) {
  const txId = str(p.transactionId, 100, "transactionId", true);
  const calc = creditCalc(p.qty, (await pricingConfig()).credit);
  if (!calc.ok) throw err("bad_request", calc.reason);
  const now = nowIso();
  const row = { id: txId, userId: ctx.uid, packageId: "create_credit", kind: "create_credit", qty: calc.qty, amount: calc.price, coins: 0, points: 0, status: "pending", createdAt: now, updatedAt: now };
  if (await fb.createOnly(`topups/${fk(txId)}`, row)) return { topup: { id: txId, status: "pending", amount: calc.price, qty: calc.qty } };
  const ex = await getRow(`topups/${fk(txId)}`);
  if (ex && ex.userId !== ctx.uid) throw err("forbidden", "transactionId นี้ถูกใช้แล้ว");
  return { topup: { id: ex.id, status: ex.status, amount: num(ex.amount), qty: num(ex.qty) } };
}

/** ยืนยันหลังจ่าย : ราคาคำนวณใหม่จาก qty ที่ผูกไว้ตอน init แล้วเทียบกับยอดจ่ายจริง ; เติมสิทธิ์ได้ครั้งเดียวต่อ transactionId */
export async function creditPackConfirm(p, ctx) {
  const txId = str(p.transactionId, 100, "transactionId", true);
  const key = `topups/${fk(txId)}`;
  const existing = await getRow(key);
  if (!existing) throw err("bad_request", "ไม่พบรายการซื้อสิทธิ์นี้ (ต้องเรียก creditPack.init หลังสร้าง QR)");
  if (existing.userId !== ctx.uid) throw err("forbidden", "รายการนี้ไม่ใช่ของคุณ");
  if (existing.kind !== "create_credit") throw err("bad_request", "รายการนี้ไม่ใช่การซื้อสิทธิ์สร้างตัวละคร");
  if (existing.status === "credited") return { credited: true, already: true, qty: num(existing.qty) };
  if (!(await acquireFlag("tx/" + txId, 60))) throw err("chat_busy", "กำลังตรวจสอบรายการนี้อยู่ ลองใหม่อีกสักครู่");
  try {
    const dep = await fetchDeposit(txId);
    if (PAID_STATUSES.indexOf(dep.status) === -1) throw err("payment_pending", "ยังไม่พบการชำระเงิน (สถานะ: " + (dep.status || "ไม่ทราบ") + ")");
    // ยอดจ่ายจริงต้องตรงกับยอดที่ล็อกไว้ตอน init (ราคาเพิ่งถูกคำนวณจาก qty ฝั่ง server ตอนนั้น ; แอดมินเปลี่ยนราคาระหว่างรอจ่ายไม่ทำให้เพี้ยน)
    if (Math.abs(dep.amount - num(existing.amount)) > 0.009) throw err("bad_request", "ยอดชำระไม่ตรงกับราคาแพ็กสิทธิ์");
    const qty = Math.trunc(num(existing.qty));
    if (qty < 1) throw err("bad_request", "รายการนี้ไม่มีจำนวนสิทธิ์");
    let claimed = false;
    const now = nowIso();
    await fb.transaction(key, (cur) => {
      claimed = false; // fn ถูกเรียกซ้ำเมื่อ ETag ชน
      if (cur && cur.status === "credited") return undefined;
      claimed = true;
      return { ...cur, status: "credited", providerStatus: dep.status, creditedAt: now, updatedAt: now };
    });
    if (!claimed) return { credited: true, already: true, qty };
    let r;
    try {
      r = await changeCredits(ctx.uid, qty, { type: "purchase", refType: "topup", refId: txId, note: `ซื้อสิทธิ์สร้างตัวละคร ${qty} ตัว (${num(existing.amount)} บาท)` });
    } catch (e) {
      await updateRow(key, { status: "pending", creditedAt: null }).catch(() => {});
      throw e;
    }
    return { credited: true, qty, added: qty, createdCredits: r.balance, balance: r.balance };
  } finally {
    await releaseFlag("tx/" + txId);
  }
}

export function creditHistory(p, ctx) {
  return pageByKey(`credit_tx/${fk(ctx.uid)}`, { limit: limitOf(p.limit, 30), cursor: p.cursor });
}

// ---------------------------------------------------------------------------
// เช็คอินรายวัน
// ---------------------------------------------------------------------------
export function currentStreak(u) {
  if (u.lastCheckIn === dayKey()) return num(u.streak);
  if (u.lastCheckIn === dayKey(-1)) return num(u.streak);
  return 0;
}

export async function checkinReward(u, streak) {
  const st = await settings(), v = await vipState(u);
  const base = Math.round(num(st.checkin_coins) * (num(v.tier.checkinMultiplier) || 1));
  const every = num(st.checkin_bonus_every, 10);
  let bonus = 0;
  if (every > 0 && streak % every === 0) bonus = v.tierIndex >= 1 ? num(st.checkin_bonus_coins_pro) : num(st.checkin_bonus_coins);
  return { base, bonus };
}

export async function checkinStatus(p, ctx) {
  const u = await getUser(ctx.uid), today = dayKey();
  const done = !!(await fb.get(`checkins/${fk(ctx.uid)}/${today}`));
  const streak = currentStreak(u);
  const every = num(await setting("checkin_bonus_every"), 10);
  const next = done ? streak : streak + 1;
  const month = today.slice(0, 7);
  const hist = (await fb.entries(`checkins/${fk(ctx.uid)}`, { orderBy: "$key", startAt: month, endAt: month + "~" })).map(([k]) => k);
  return {
    today, canClaim: !done, streak, longestStreak: num(u.longestStreak), lastCheckIn: u.lastCheckIn || null,
    nextBonusIn: every > 0 ? every - (streak % every) : null, todayReward: await checkinReward(u, next), history: hist.reverse(),
  };
}

export async function checkinClaim(p, ctx) {
  const today = dayKey();
  const u = await getUser(ctx.uid);
  const streak = u.lastCheckIn === dayKey(-1) ? num(u.streak) + 1 : 1;
  const rw = await checkinReward(u, streak), total = rw.base + rw.bonus;
  const now = nowIso();
  // เขียนได้เฉพาะตอนยังไม่มี -> วันละครั้งแน่นอน แม้กดซ้ำ/หลายแท็บ
  const created = await fb.createOnly(`checkins/${fk(ctx.uid)}/${today}`, { id: today, userId: ctx.uid, date: today, coins: rw.base, bonus: rw.bonus, streak, createdAt: now, updatedAt: now });
  if (!created) throw err("already_done", "วันนี้เช็คอินไปแล้ว");
  await updateRow(`users/${fk(ctx.uid)}`, (cur) => ({ streak, longestStreak: Math.max(num(cur.longestStreak), streak), lastCheckIn: today }));
  const r = await changeCoins(ctx.uid, total, { usageType: "checkin", refType: "checkin", refId: today, note: "เช็คอินวันที่ " + streak });
  return { coins: rw.base, bonus: rw.bonus, streak, balance: r.balance };
}

// ---------------------------------------------------------------------------
// โค้ดโปรโมชัน
// ---------------------------------------------------------------------------
export async function promoRedeem(p, ctx) {
  const code = str(p.code, 40, "โค้ด", true).toUpperCase();
  if (!/^[A-Z0-9_-]+$/.test(code)) throw err("bad_request", "โค้ดไม่ถูกต้อง");
  rateLimit("promo:" + ctx.uid, 10, 60);
  const pc = await getRow(`promo_codes/${fk(code)}`);
  if (!pc || pc.isActive === false) throw err("bad_request", "โค้ดไม่ถูกต้อง");
  if (pc.expiresAt && new Date(pc.expiresAt).getTime() < Date.now()) throw err("bad_request", "โค้ดนี้หมดอายุแล้ว");
  const now = nowIso();
  // 1) จองสิทธิ์ต่อผู้ใช้ (ใช้ซ้ำไม่ได้)
  if (!(await fb.createOnly(`promo_redemptions/${fk(code)}/${fk(ctx.uid)}`, { id: ctx.uid, userId: ctx.uid, code, coins: num(pc.coins), createdAt: now, updatedAt: now }))) {
    throw err("already_done", "คุณใช้โค้ดนี้ไปแล้ว");
  }
  // 2) นับจำนวนใช้แบบ atomic (ครบจำนวนแล้วคืนสิทธิ์)
  try {
    const r = await fb.transaction(`promo_codes/${fk(code)}/usedCount`, (cur) => (num(pc.maxUses) > 0 && num(cur) >= num(pc.maxUses) ? undefined : num(cur) + 1));
    if (!r.committed) throw err("bad_request", "โค้ดนี้ถูกใช้ครบจำนวนแล้ว");
  } catch (e) {
    await fb.remove(`promo_redemptions/${fk(code)}/${fk(ctx.uid)}`).catch(() => {});
    throw e;
  }
  const r = await changeCoins(ctx.uid, num(pc.coins), { usageType: "redeem", refType: "promo", refId: code, note: "ใช้โค้ด " + code });
  return { coins: num(pc.coins), balance: r.balance };
}

// ---------------------------------------------------------------------------
// ชวนเพื่อน
// ---------------------------------------------------------------------------
const referralsOf = async (inviterId) => rowsOf(await fb.get("referrals", { orderBy: "inviterId", equalTo: inviterId }));

export async function referralInfo(p, ctx) {
  const u = await getUser(ctx.uid);
  const list = (await referralsOf(ctx.uid)).sort((a, b) => (String(b.createdAt) < String(a.createdAt) ? -1 : 1)).slice(0, 50);
  return {
    code: u.referralCode, invited: list.length, maxInvites: num(await setting("referral_max_invites")),
    earned: list.reduce((s, r) => s + num(r.coinsInviter), 0),
    history: list.map((r) => pick(r, ["inviteeId", "coinsInviter", "createdAt"])), referredBy: u.referredBy || null,
  };
}

export async function referralApply(p, ctx) {
  const code = str(p.code, 20, "โค้ดชวนเพื่อน", true).toUpperCase();
  rateLimit("ref:" + ctx.uid, 5, 60);
  const me = await getUser(ctx.uid);
  if (me.referredBy || (await fb.get(`referrals/${fk(ctx.uid)}`))) throw err("already_done", "คุณใช้โค้ดชวนเพื่อนไปแล้ว");
  const inviterId = /^[A-Z0-9]+$/.test(code) ? await fb.get(`referral_codes/${code}`) : null;
  if (!inviterId) throw err("bad_request", "โค้ดชวนเพื่อนไม่ถูกต้อง");
  if (inviterId === ctx.uid) throw err("bad_request", "ใช้โค้ดของตัวเองไม่ได้");
  const st = await settings();
  if ((await referralsOf(inviterId)).length >= num(st.referral_max_invites)) throw err("bad_request", "โค้ดนี้ถูกใช้ครบจำนวนแล้ว");
  const a = num(st.referral_inviter_coins), b = num(st.referral_invitee_coins), now = nowIso();
  const created = await fb.createOnly(`referrals/${fk(ctx.uid)}`, { id: ctx.uid, inviterId, inviteeId: ctx.uid, code, coinsInviter: a, coinsInvitee: b, createdAt: now, updatedAt: now });
  if (!created) throw err("already_done", "คุณใช้โค้ดชวนเพื่อนไปแล้ว");
  await updateRow(`users/${fk(ctx.uid)}`, { referredBy: inviterId });
  const r = await changeCoins(ctx.uid, b, { usageType: "invite", refType: "referral", refId: inviterId, note: "กรอกโค้ดชวนเพื่อน" });
  if (a > 0) await changeCoins(inviterId, a, { usageType: "invite", refType: "referral", refId: ctx.uid, note: "ชวนเพื่อนสำเร็จ" });
  await notify(inviterId, "system", "ชวนเพื่อนสำเร็จ", "ได้รับ " + a + " คอยน์");
  return { coins: b, balance: r.balance };
}

export async function giftsList() {
  return { items: (await readAll("gifts")).filter((g) => g.isActive !== false).sort((a, b) => num(a.sort) - num(b.sort)) };
}

export function coinsHistory(p, ctx) {
  return pageByKey(`coin_tx/${fk(ctx.uid)}`, {
    limit: limitOf(p.limit, 30), cursor: p.cursor,
    filter: p.usageType ? (r) => r.usageType === p.usageType : null,
  });
}
